import { DragAndDropMultiFileUploaderContext } from "@app/shared/DragAndDropMultiFileUploaderContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { UploaderProps, DragAndDropFileUploaderStateEnum } from "@app/shared/interfaces/dragAndDropUploader";
import { QESButton } from "@app/shared/qes-standards/Button";
import cx from "classnames";
import { uniqueId } from "lodash";
import * as React from "react";
import { JSX, useContext } from "react";

const inputId = uniqueId("qes-file-upload-");

const DragAndDropMultiFileUploader = (props: UploaderProps): JSX.Element => {
  const [boxState, setBoxState] = React.useState<DragAndDropFileUploaderStateEnum>(DragAndDropFileUploaderStateEnum.Neutral);
  const [error, setError] = React.useState<string[]>([]);
  const [inputKey, setInputKey] = React.useState(Date.now());
  const boxRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const multiFileUploaderContext = useContext(DragAndDropMultiFileUploaderContext);

  React.useEffect(() => {
    if (!!props.onUploadStateChange) {
      props.onUploadStateChange(boxState);
    }
  }, [boxState]);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    // prevent default is necessary to allow the drop event later
    // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#droptargets
    e.preventDefault();
    setBoxState(DragAndDropFileUploaderStateEnum.Hover);
  };

  const handleBrowseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files.length) {
      setBoxState(DragAndDropFileUploaderStateEnum.Dropped);
      multiFileUploaderContext.setSelectedFiles(e.target.files);
      setInputKey(Date.now());
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setBoxState(DragAndDropFileUploaderStateEnum.Dropped);
    multiFileUploaderContext.setSelectedFiles(e.dataTransfer.files);
    setInputKey(Date.now());
  };

  React.useEffect(() => {
    if (props.hasBeenSuccessful) {
      setBoxState(DragAndDropFileUploaderStateEnum.Success);
    }
  }, [props.hasBeenSuccessful]);

  const handleUpload = async () => {
    const promises = Array.from(multiFileUploaderContext.selectedFiles).map((file => 
      uploadAttachment(file)
    ));
    try {
      await Promise.all(promises);
      setBoxState(DragAndDropFileUploaderStateEnum.Success);
      setError([]);
    }
    catch (errorResponse) {
      if (typeof errorResponse === "string") {
        setError([errorResponse]);
      } else {
        setError(errorResponse);
      }
      setBoxState(DragAndDropFileUploaderStateEnum.Error);
    }
  }

  const uploadAttachment = (file: File) => {
    try {
      props.onMultipleUpload(file).then(
        () => {
        }, (errorResponse?: string[] | string) => {
          if (typeof errorResponse === "string") {
            setError([errorResponse]);
          } else {
            setError(errorResponse);
          }
          setBoxState(DragAndDropFileUploaderStateEnum.Error);
          inputRef.current.value = null;
        },
      );
    } catch (error) {
      setBoxState(DragAndDropFileUploaderStateEnum.Error);
      inputRef.current.value = null;
    }

  };

  const removeFile = (fileKey: number) => {
    multiFileUploaderContext.setUploadedFiles(prevFiles  => {
      const newFiles = new Map(prevFiles);
      newFiles.delete(fileKey);
      return newFiles;
    });
    props.onRemove(fileKey);
  };

  const areThereInvalidFileTypes = () : boolean=> {
    let areThereInvalidFiles = false;
    const allowedTypes = props.filetype && props.filetype.split(",");
    if (!allowedTypes) return areThereInvalidFiles;
    Array.from(multiFileUploaderContext.selectedFiles).forEach(file => {
      if (!allowedTypes.includes(`.${file.name.split(".").pop()}`)){
        areThereInvalidFiles = true;
        return;
      }
    });
    return areThereInvalidFiles;
  }

  const getBoxCopy = React.useMemo(() => {
    switch (boxState) {
      case DragAndDropFileUploaderStateEnum.Neutral:
        return <>Drag your files here to upload or <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">Browse</label>.</>;
      case DragAndDropFileUploaderStateEnum.Hover:
        return <>Drop your files here.</>;
      case DragAndDropFileUploaderStateEnum.Dropped:
        if (areThereInvalidFileTypes()) {
          setBoxState(DragAndDropFileUploaderStateEnum.TypeError);
          return;
        }
        handleUpload();
        return <>We are uploading and processing your files.</>;
      case DragAndDropFileUploaderStateEnum.Error:
        if (error?.length) {
          return (
            <>
              There was an error processing your files:<br />
              <div className="mb-1 mt-1">{error.length === 1 ? error[0] : (<ul className="d-inline-block ps-0 mb-0 text-start">{error.map((e, i) => <li key={i}>{e}</li>)}</ul>)}</div>
              Please <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">try again</label>.
            </>
          );
        }
        return <>There was an error processing your files. Please <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">try again</label>.</>;
      case DragAndDropFileUploaderStateEnum.Success:
        setTimeout(() => { 
          setBoxState(DragAndDropFileUploaderStateEnum.Neutral);
        }, 3000);
        return (
          <>Your file{(!!multiFileUploaderContext && multiFileUploaderContext.selectedFiles.length > 1) ? "s" : ""} {(!!multiFileUploaderContext.selectedFiles && multiFileUploaderContext.selectedFiles.length > 1) ? "were" : "was"} successfully uploaded.</>
        );
      case DragAndDropFileUploaderStateEnum.TypeError:
        return <>Please check your files are one of the following types and <label htmlFor={inputId} data-testid={props.testId} className="qes-file-uploader__label mb-0">try again</label>:<br /> {props.filetype}</>;
      default:
        return null;
    }
  }, [boxState]);

  return (
    <>
      <div
        className={cx({
          "qes-file-uploader": true,
          "small": props.small,
        })}
      >
        {props.title && <h3 className="qes-file-uploader__title">{props.title}</h3>}
        <input type="file" multiple id={inputId} key={inputKey} className="qes-file-uploader__input" accept={props.filetype || null} onChange={(e) => handleBrowseChange(e)} ref={inputRef} />
        <div
          className={cx({
            "error": boxState === DragAndDropFileUploaderStateEnum.Error || boxState === DragAndDropFileUploaderStateEnum.TypeError,
            "hover": boxState === DragAndDropFileUploaderStateEnum.Hover,
            "qes-file-uploader__drop-box": true,
            "success": boxState === DragAndDropFileUploaderStateEnum.Success,
          })}
          onDragLeave={() => setBoxState(DragAndDropFileUploaderStateEnum.Neutral)}
          onDragEnter={(e) => handleDrag(e)}
          onDragOver={(e) => handleDrag(e)}
          onDrop={(e) => handleDrop(e)}
          ref={boxRef}
        >
          <span role="alert" aria-live="assertive">
            {getBoxCopy}
          </span>
        </div>
      </div>
      <div>
        {!!multiFileUploaderContext.uploadedFiles && multiFileUploaderContext.uploadedFiles.size > 0 && 
          <div className="qes-multi-file-uploader_attachment-list">
            <label id="attachment-subtitle">Attachments</label>
            <ul>
              {Array.from(multiFileUploaderContext.uploadedFiles).map(([key, file]) => 
                <li key={key}>
                  {!!props.handleDownloadLink ? (<a href="#" onClick={() => props.handleDownloadLink(key)}>{file.name}</a>) : file.name }
                  <QESButton
                    qesType="secondary"
                    qesTestId={`remove-attachment--${file.name}--${key}`}
                    onClick={() => removeFile(key)}
                  >      
                    <QesIcon iconName={FontAwesome.Trash} width="12" height="12" className="remove-attachment" color="#cc3a52" description="Remove attachment" />
                  </QESButton>
                </li>
              )}
            </ul>
          </div>
        }
      </div>
    </>
  );
};

export default DragAndDropMultiFileUploader;
